import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, ColorPicker, ColorPickerProps } from "antd";
import { I18N_NS } from "Config/i18n";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Editor, Transforms, Element, Text, Range } from "slate";
import { ReactEditor, useSlate } from "slate-react";
import { hasSelection } from "../utils/hasSelection";

interface TextColorProps {
  disabled?: boolean;
}

const setBackgroundColor = (editor: Editor, color: string) => {
  const newProperties = { background: color || undefined };
  if (hasSelection(editor)) {
    // Apply font size to the selected text
    Transforms.setNodes(editor, newProperties, { at: editor.selection, match: (n) => Text.isText(n), split: true });
  }
  ReactEditor.focus(editor);
};

export const TextBackground = ({ disabled }: TextColorProps) => {
  const { t } = useTranslation(I18N_NS.RICH_EDITOR);
  const editor = useSlate();
  const [color, setColor] = useState(null);
  const [open, setOpen] = useState(false);

  const handleChangeComplete: ColorPickerProps["onChangeComplete"] = (colorValue) => {
    const newColor = colorValue?.toHexString();
    // if color is without opacity, because opacity means that the "clear" was pressed
    if (newColor?.length <= 7) {
      const hex = colorValue.toHexString();
      setColor(hex);
      setBackgroundColor(editor, hex);
      Editor.addMark(editor, "background", hex);
    } else {
      setColor(null);
      setBackgroundColor(editor, null);
      Editor.removeMark(editor, "color");
    }
  };

  useEffect(() => {
    const selectedNodes = Array.from(
      Editor.nodes(editor, {
        at: editor.selection,
        match: (n) => !!(n as Element | Text).background,
      })
    );
    if (selectedNodes.length) {
      const currentBackground = (selectedNodes[0][0] as Element)?.background as string;
      if (Range.isCollapsed(editor.selection) && color !== currentBackground) {
        setColor(currentBackground || null);
        if (currentBackground) {
          Editor.addMark(editor, "background", currentBackground);
        }
      }
    } else if (color) {
      setColor(null);
    }
  }, [editor?.selection]);

  return (
    <ColorPicker
      arrow={false}
      format="hex"
      value={color}
      onChangeComplete={handleChangeComplete}
      disabled={disabled}
      disabledAlpha
      open={open}
      panelRender={(panel) => (
        <div className="text-right">
          <Button
            className="mb-2"
            size="small"
            onClick={() => {
              setOpen(false);
              setBackgroundColor(editor, null);
              setColor(null);
              Editor.removeMark(editor, "color");
            }}
          >
            {t("Reset")}
          </Button>
          {panel}
        </div>
      )}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
      }}
    >
      <div className="relative" style={{ margin: "0 3px" }}>
        <FontAwesomeIcon
          title={t("Text background")}
          fixedWidth
          icon={["far", "highlighter"]}
          className="pointer"
          onClick={() => setOpen(!open)}
        />
        <div className="slate--background-tool" style={{ background: color }} />
      </div>
    </ColorPicker>
  );
};
